import * as React from "react"
import { Box, Button, Flex, Heading, Link, Stack, Text } from "@chakra-ui/react"

import { useForm } from "lib/hooks/useForm"
import yup from "lib/yup"

import { Checkbox } from "./Checkbox"
import { Form } from "./Form"
import { Input } from "./Input"

export interface RegisterExtraInfo {
  isParentNotifiedDisabled: boolean
  isOver16: boolean
  parentEmail: string | null
  isPrivacyCheckAccepted: boolean
}

interface Props {
  handleRegister: (values: RegisterExtraInfo) => void
}

const RegisterExtraSchema = yup.object().shape({
  isOver16: yup.boolean(),
  isParentNotifiedDisabled: yup.boolean(),
  parentEmail: yup
    .string()
    .email("Ongeldig e-mailadres")
    .when(["isParentNotifiedDisabled", "isOver16"], {
      is: false,
      then: (schema) => schema.required("E-mailadres van ouders/verzorgers verplicht"),
      otherwise: (schema) => schema.nullIfEmpty(),
    }),
  isPrivacyCheckAccepted: yup
    .boolean()
    .oneOf([true], "Je moet voorwaarden accepteren om te kunnen registreren"),
})

export function RegisterExtraInfoForm(props: Props) {
  const defaultValues = {
    isOver16: false,
    isParentNotifiedDisabled: false,
    parentEmail: "",
    isPrivacyCheckAccepted: false,
  }

  const form = useForm({ defaultValues, schema: RegisterExtraSchema })

  const handleSubmit = (data: typeof defaultValues) => {
    return props.handleRegister(data)
  }

  const isOver16 = form.watch("isOver16") as boolean
  const isParentNotifiedDisabled = form.watch("isParentNotifiedDisabled") as boolean

  return (
    <Form {...form} onSubmit={handleSubmit} id="extra-info-form">
      <Stack py={10}>
        <Text>
          LEUK DAT JE JE WIL AANMELDEN VOOR KUNSTBENDE! Voordat we je aanmelding definitief kunnen maken,
          hebben we nog een paar vragen voor je.
          <br />
          <br />
          Ben je jonger dan 16 jaar? Dan is Kunstbende verplicht jouw ouder(s) of verzorger(s) te informeren
          over jouw deelname.
        </Text>
        <Flex flexDir="column" justifyContent="space-between">
          <Stack spacing={5} p={2}>
            <Flex flexDir="row">
              <Checkbox
                label="Ik ben ouder dan 16 jaar"
                name="isOver16"
                mb={0}
                colorScheme="yellow"
                size="lg"
              />
              {/* <InputLabel label="Ik ben ouder dan 16 jaar" ml={2} /> */}
            </Flex>
            {!isOver16 && (
              <Stack spacing={5} ml={5} pl={10} borderLeft="2px solid">
                {!isParentNotifiedDisabled && (
                  <Input
                    label="Dit is het mailadres van mijn ouder/verzorger"
                    name="parentEmail"
                    placeholder="test@example.com"
                  />
                )}
                <Flex flexDir="row">
                  <Checkbox
                    label="Ik wil niet dat mijn ouder of verzorger op de hoogte is van mijn deelname"
                    name="isParentNotifiedDisabled"
                    mb={0}
                    colorScheme="yellow"
                    size="lg"
                  />
                </Flex>
              </Stack>
            )}
            <Heading as="h3" size="md">
              Privacy & Data
            </Heading>
            <Stack>
              <Checkbox
                name="isPrivacyCheckAccepted"
                label={
                  <Text>
                    lk heb de{" "}
                    <Link
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="yellow.500"
                      aria-label="privacyvoorwaarden pagina (wordt geopend in een nieuw tabblad)"
                    >
                      privacyvoorwaarden
                    </Link>{" "}
                    van Kunstbende gelezen en ga hiermee akkoord
                  </Text>
                }
                colorScheme="yellow"
                size="lg"
              />
              <Text fontSize="sm">
                Lees{" "}
                <Link
                  href="/veiligheid"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="yellow.500"
                  aria-label="veiligheid page (opens in a new page)"
                >
                  hier
                </Link>{" "}
                meer over de veiligheid bij Kunstbende.
              </Text>
            </Stack>
          </Stack>
          <Box pt={6}>
            <Button
              colorScheme="yellow"
              type="submit"
              isDisabled={form.formState.isSubmitting}
              isLoading={form.formState.isSubmitting}
              w="100%"
            >
              Bevestigen
            </Button>
          </Box>
        </Flex>
      </Stack>
    </Form>
  )
}
